import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  rulerMetricsSelector,
  rulerRangeSelector,
} from '../selectors/ruler.selectors';
import { isToolActiveSelector } from '../../tool/selectors/tool.selectors';
import { Tool } from '../../tool/utils/tool.utils';
import {
  Box,
  Divider,
  HStack,
  IconButton,
  LockIcon,
  LockOpenIcon,
} from '@cdl/heartbeat-react';
import { setActiveTool } from '../../tool/actions/tool.actions';
import { squareToPixel } from '../../plot/utils/graph.utils';
import { useStrip } from '../../plot/hooks/useStrip';
import { computeInterval } from '../../episodeMeasurement/utils/episode-measurement.utils';
import { roundedDurationSelector } from '../../ecg/selectors/ecg.selectors';
import { clamp } from 'lodash';
import { trackLockRuler } from '../../analytics/thunks/track-lock-ruler.thunk';
import { usePlot } from '../../plot/contexts/plot.context';

export const RULER_HEADER_HEIGHT = squareToPixel(2);
const INFO_WIDTH = squareToPixel(7.5);
const INFO_HEIGHT = squareToPixel(2);
const FONTSIZE = '1.125rem';

interface Props {
  hasRulerLock?: boolean;
}

export const RulerInfo: React.FC<Props> = ({ hasRulerLock = true }) => {
  const { xScale, height, onset, offset } = useStrip();
  const { plotName } = usePlot();
  const dispatch = useDispatch();
  const isRulerAnchored = useSelector(isToolActiveSelector(Tool.rulerAnchored));
  const isRulerActive = useSelector(
    isToolActiveSelector(Tool.ruler, Tool.rulerAnchored),
  );
  const duration = useSelector(roundedDurationSelector);
  const rulerInfo = useSelector(rulerMetricsSelector);
  const [rulerStart, rulerEnd] = useSelector(rulerRangeSelector);
  const rulerDuration = computeInterval(rulerStart, rulerEnd);
  const x = rulerStart + rulerDuration / 2;

  // Made the ruler info reposition in case if it gets out of graph
  const xPosition = useMemo(() => {
    return clamp(
      xScale(x) - INFO_WIDTH / 2,
      0,
      xScale(duration + onset + offset) - INFO_WIDTH,
    );
  }, [duration, x, xScale, onset, offset]);

  const handleLock = () => {
    const tool = isRulerAnchored ? Tool.ruler : Tool.rulerAnchored;
    if (!isRulerAnchored) {
      dispatch(trackLockRuler(plotName));
    }

    dispatch(
      setActiveTool({
        tool: tool,
        meta: { history: false },
      }),
    );
  };

  if (rulerDuration === 0 || !isRulerActive) {
    return <></>;
  }

  return (
    <foreignObject
      width={INFO_WIDTH}
      height={INFO_HEIGHT}
      x={xPosition}
      y={height - squareToPixel(1.5)}
      fontSize={FONTSIZE}
    >
      <HStack
        bgColor='neutral.300'
        borderRadius='4px'
        color='white'
        justifyContent='center'
        opacity={0.9}
        userSelect='none'
        divider={<Divider orientation='vertical' height='1.5rem' />}
        data-testid='ruler-info'
      >
        <Box>{rulerInfo}</Box>
        {hasRulerLock && (
          <>
            <IconButton
              aria-label='lock'
              size='sm'
              variant='tertiary'
              onClick={handleLock}
              m='xs'
            >
              {isRulerAnchored ? (
                <LockIcon color='primary.500' fontSize='l' />
              ) : (
                <LockOpenIcon color='neutral.0' fontSize='l' />
              )}
            </IconButton>
          </>
        )}
      </HStack>
    </foreignObject>
  );
};
